import React, { useState } from "react";
import { Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useOutlookSourceSettingsText,
  useOutlookSourceSettingsViewModel,
} from "@onetasklist/shared/sources/outlook";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface OutlookCalendarSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const OutlookCalendarSourceSettings: React.FC<
  OutlookCalendarSourceSettingsProps
> = ({ editingConnection, onUpdate, sectionOffset }) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const vm = useOutlookSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });
  const text = useOutlookSourceSettingsText(t, "calendar");
  const [categoryQuery, setCategoryQuery] = useState("");

  if (!vm.isVisible || !vm.isOutlookCalendar) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.outlookcalendar.sectionCalendar",
      )}
    >
      <Field orientation="horizontal" label={text.labelText} hint={text.hintText}>
        <SourceSingleTagPicker
          isLoading={vm.isLoading}
          selectedId={
            vm.selectedCategoryId === vm.noneOptionValue
              ? undefined
              : vm.selectedCategoryId
          }
          selectedLabel={vm.selectedCategoryName}
          query={categoryQuery}
          placeholder={text.notSetText}
          options={vm.categoryOptions.map((category) => ({
            id: category.id,
            label: category.label,
          }))}
          onQueryChange={setCategoryQuery}
          onSelect={vm.onSelectCategory}
          clearSelectionText={text.notSetText}
        />
        {vm.isLoading && <Text>{text.loadingText}</Text>}
        {vm.hasError && <Text>{text.loadErrorText}</Text>}
      </Field>
    </SettingsSectionCard>
  );
};
